import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'teacher') {
        navigate('/teachers/dashboard');
      } else {
        navigate('/students/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="mx-auto flex min-h-screen max-w-7xl items-stretch px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <div className="hidden lg:flex lg:w-1/2 lg:pr-8">
          <div className="relative flex w-full flex-col justify-between overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 via-indigo-700 to-slate-900 p-10 text-white shadow-2xl">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-cyan-300/20 blur-3xl" />
            <div className="absolute left-10 top-36 h-px w-24 bg-white/30" />
            <div className="absolute left-10 top-40 h-px w-12 bg-white/20" />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm">
                <AcademicCapIcon className="h-5 w-5" />
                DEBEST College
              </div>
              <h2 className="mt-8 text-4xl font-bold leading-tight xl:text-5xl">
                Learn with confidence.
                <br />
                Move forward together.
              </h2>
              <p className="mt-5 max-w-md text-base text-blue-100 xl:text-lg">
                A calm, familiar place where students and teachers connect, share lessons, and make daily progress.
              </p>
              <p className="mt-4 max-w-md text-sm italic text-blue-200">
                “Small steps each day become real academic growth.”
              </p>

            </div>

            <div className="relative z-10 grid grid-cols-1 gap-4 text-sm">
              <div className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
                <p className="text-xs uppercase tracking-wide text-blue-200">Support</p>
                <p className="mt-1 text-2xl font-bold">1:1</p>
                <p className="mt-1 text-blue-100">Teacher feedback</p>
              </div>
            </div>

            <div className="relative z-10 mt-4 flex items-center justify-between rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-sm">
              <div className="flex -space-x-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-slate-900 bg-blue-200 text-xs font-semibold text-slate-900">
                  TA
                </span>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-slate-900 bg-indigo-200 text-xs font-semibold text-slate-900">
                  ST
                </span>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-slate-900 bg-cyan-200 text-xs font-semibold text-slate-900">
                  DC
                </span>
              </div>
              <p className="text-sm text-blue-100">A real school community, online.</p>
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-center lg:w-1/2">
          <div className="w-full max-w-md rounded-3xl border border-white/60 bg-white/90 p-8 shadow-xl backdrop-blur-md sm:p-10">
            <div className="mb-8 text-center">
              <img src="/debestlogo.png" alt="DeBest College logo" className="mx-auto mb-5 h-20 w-20 rounded-2xl object-cover shadow-md" />
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">Welcome back</h1>
              <p className="mt-2 text-sm text-slate-600">Sign in to continue to your DeBest College learning portal.</p>
            </div>

            {error && (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3.5">
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-semibold text-slate-700">
                  Email address
                </label>
                <div className="group relative">
                  <EnvelopeIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-blue-600" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-semibold text-slate-700">
                  Password
                </label>
                <div className="group relative">
                  <LockClosedIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-blue-600" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-11 pr-12 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-500 transition hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Secure sign-in</span>
                <Link to="/forgot-password" className="font-medium text-blue-700 transition hover:text-blue-800">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                className="w-full rounded-xl bg-blue-700 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign in to dashboard'}
              </button>
            </form>

            <div className="mt-7 text-center">
              <p className="text-sm text-slate-600">Don&apos;t have an account?</p>
              <Link to="/signup" className="mt-2 inline-flex items-center text-sm font-semibold text-blue-700 transition hover:text-blue-800">
                Create an account
                <span className="ml-1" aria-hidden="true">
                  →
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
